import { buildZoneHierarchy } from 'features/Responder/features/Zones/utils/zoneHierarchy';
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import {
  getDuplicateNameMessage,
  hasDuplicateEntityName,
} from 'features/Responder/utils/entityNameValidation';
import apiClient from 'services/_infra/apiClient';
import { notifyErrorToast, notifySuccessToast } from 'utils/notifications/toast.utils';

/**
 * @typedef {{ lat: number, lng: number }} Coord
 * @typedef {{ fillColor?: string, strokeColor?: string, fillOpacity?: number }} ZoneRenderParams
 * @typedef {{ occupancyMin?: number|null, occupancyMax?: number|null, timeOfStayMinutes?: number|null, debounceSeconds?: number|null }} ZoneParameters
 * @typedef {{ personnelIds?: number[], teamIds?: number[] }} ZoneAssignments
 * @typedef {{ notes?: string, tags?: string[] }} ZoneMetadata
 * @typedef {{
 *   id?: number|string,
 *   name: string,
 *   polygon: Coord[],
 *   renderParams?: ZoneRenderParams,
 *   locked?: boolean,
 *   parentId?: number|null,
 *   childrenIds?: number[],
 *   groupId?: string|number|null,
 *   parameters?: ZoneParameters,
 *   assignments?: ZoneAssignments,
 *   metadata?: ZoneMetadata,
 *   createdAt?: string,
 *   updatedAt?: string,
 * }} ZonePayload
 */

/**
 * Lightweight zones state for responder map tooling.
 * Stores in-memory zones plus helpers to select and mutate while backend wiring is pending.
 */
const ZonesContext = createContext(null);

const getPolygonPoints = (zone) => {
  if (Array.isArray(zone?.polygon)) return zone.polygon;
  return [];
};

const coerceZonePolygon = (zone, fallbackPolygon) => {
  if (!zone || typeof zone !== 'object') return zone;
  const defaultFillOpacity = 0.15;
  const polygon = Array.isArray(zone.polygon)
    ? zone.polygon
    : Array.isArray(zone.geometry?.coord)
      ? zone.geometry.coord
      : Array.isArray(zone.geometry?.points)
        ? zone.geometry.points
        : Array.isArray(fallbackPolygon)
          ? fallbackPolygon
          : undefined;
  const { geometry, ...rest } = zone;
  const fillOpacity = defaultFillOpacity;
  const fillColor = rest.fillColor || rest.strokeColor || '#f97316';
  const strokeColor = rest.strokeColor || rest.fillColor || fillColor;
  const base = polygon !== undefined ? { ...rest, polygon } : { ...rest };
  return { ...base, polygon, fillOpacity, fillColor, strokeColor, locked: !!rest.locked };
};

const normalizePoint = (pt) => {
  if (!pt) return null;
  const lat = Number(pt.lat ?? pt.latitude);
  const lng = Number(pt.lng ?? pt.longitude);
  return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
};

const buildRenderParams = (zone) => {
  const fillColor = zone.fillColor || zone.strokeColor || '#f97316';
  const strokeColor = zone.strokeColor || zone.fillColor || fillColor;
  const fillOpacity = Number.isFinite(zone.fillOpacity) ? zone.fillOpacity : 0.15;
  return { fillColor, strokeColor, fillOpacity };
};

const buildParameters = (zone) => {
  if (zone.parameters) return zone.parameters;
  const { occupancyMin, occupancyMax, timeOfStayMinutes, debounceSeconds } = zone;
  if (
    occupancyMin != null ||
    occupancyMax != null ||
    timeOfStayMinutes != null ||
    debounceSeconds != null
  ) {
    return { occupancyMin, occupancyMax, timeOfStayMinutes, debounceSeconds };
  }
  return undefined;
};

const buildAssignments = (zone) => {
  if (zone.assignments) return zone.assignments;
  const { personnelIds, teamIds } = zone;
  if (Array.isArray(personnelIds) || Array.isArray(teamIds)) {
    return {
      personnelIds: Array.isArray(personnelIds) ? personnelIds : undefined,
      teamIds: Array.isArray(teamIds) ? teamIds : undefined,
    };
  }
  return undefined;
};

const buildMetadata = (zone) => {
  if (zone.metadata) return zone.metadata;
  const { notes, tags } = zone;
  if (notes != null || Array.isArray(tags)) {
    return { notes, tags };
  }
  return undefined;
};

const getPersistedId = (zone) => {
  if (!zone) return null;
  if (zone.serverId != null) return zone.serverId;
  const numericId = Number(zone.id);
  return Number.isFinite(numericId) ? zone.id : null;
};

/** @returns {ZonePayload|null} */
const serializeZone = (zone) => {
  if (!zone || typeof zone !== 'object') return null;
  const points = Array.isArray(zone.polygon)
    ? zone.polygon.map(normalizePoint).filter(Boolean)
    : [];
  const renderParams = buildRenderParams(zone);
  const parameters = buildParameters(zone);
  const assignments = buildAssignments(zone);
  const metadata = buildMetadata(zone);

  return {
    id: zone.id,
    name: zone.name,
    polygon: points,
    renderParams,
    locked: !!zone.locked,
    parentId: zone.parentId ?? null,
    childrenIds: zone.childrenIds,
    groupId: zone.groupId ?? null,
    parameters,
    assignments,
    metadata,
    createdAt: zone.createdAt,
    updatedAt: zone.updatedAt,
  };
};

const serializeZones = (zones = []) =>
  Array.isArray(zones) ? zones.map(serializeZone).filter(Boolean) : [];

const collectDescendantZoneKeys = (allZones = [], rootKey) => {
  if (!Array.isArray(allZones) || rootKey == null) return [];
  const childrenByParent = new Map();
  allZones.forEach((zone) => {
    const parentKey = zone?.parentId == null ? null : `${zone.parentId}`;
    if (!parentKey) return;
    const next = childrenByParent.get(parentKey) || [];
    next.push(`${zone.id}`);
    childrenByParent.set(parentKey, next);
  });
  const descendants = [];
  const stack = [`${rootKey}`];
  while (stack.length) {
    const current = stack.pop();
    const children = childrenByParent.get(current) || [];
    children.forEach((childKey) => {
      descendants.push(childKey);
      stack.push(childKey);
    });
  }
  return descendants;
};

export const ZonesProvider = ({
  children,
  initialZones = [],
  shouldFetchZonesWithTours = true,
}) => {
  const [zones, setZones] = useState(() =>
    Array.isArray(initialZones) ? initialZones.map((z) => coerceZonePolygon(z, [])) : [],
  );
  const [zonesWithTours, setZonesWithTours] = useState([]);
  const [activeZoneId, setActiveZoneId] = useState(null);
  const [selectedZoneIds, setSelectedZoneIds] = useState([]);
  const [zonesEditing, setZonesEditing] = useState(false);
  const [zoneEditMode, setZoneEditMode] = useState('select'); // select | reshape | resize | addVertex | eraseVertex
  const [, setGeometryHistory] = useState({}); // { [id]: { past: LatLng[][], future: LatLng[][] } }

  const zonesWithMeta = useMemo(() => buildZoneHierarchy(zones), [zones]);

  useEffect(() => {
    if (activeZoneId == null && Array.isArray(zones) && zones.length) {
      setActiveZoneId(zones[0].id ?? null);
    }
  }, [activeZoneId, zones]);

  const addZone = (zone) => {
    if (!zone) return null;
    const id = zone.id ?? crypto.randomUUID?.() ?? `zone-${Date.now()}`;
    const normalized = { ...coerceZonePolygon({ ...zone, id }, []), isModified: true };
    setZones((prev) => {
      const next = prev.some((z) => `${z.id}` === `${id}`)
        ? prev.map((z) => (`${z.id}` === `${id}` ? { ...z, ...normalized } : z))
        : [...prev, normalized];
      return next;
    });
    return id;
  };

  const updateZone = (id, updates) => {
    if (id == null || !updates) return;
    setZones((prev) =>
      prev.map((z) =>
        `${z.id}` === `${id}`
          ? { ...coerceZonePolygon({ ...z, ...updates }, z.polygon), isModified: true }
          : z,
      ),
    );
  };

  const pushGeometryHistory = (id, points) => {
    if (id == null || !Array.isArray(points)) return;
    setGeometryHistory((prev) => {
      const entry = prev[id] || { past: [], future: [] };
      return {
        ...prev,
        [id]: { past: [...entry.past, points], future: [] },
      };
    });
  };

  const setZoneGeometry = (id, points) => {
    if (id == null || !Array.isArray(points)) return;
    setZones((prev) =>
      prev.map((z) =>
        `${z.id}` === `${id}`
          ? (() => {
              const { geometry: _omitGeometry, ...rest } = z;
              return { ...rest, polygon: points };
            })()
          : z,
      ),
    );
  };

  const undoZoneGeometry = (id) => {
    if (id == null) return;
    setGeometryHistory((prev) => {
      const entry = prev[id];
      if (!entry || !entry.past.length) return prev;
      const past = [...entry.past];
      const nextPoints = past.pop();
      const current = getPolygonPoints(zones.find((z) => `${z.id}` === `${id}`));
      const future = current ? [current, ...(entry.future || [])] : entry.future || [];
      setZoneGeometry(id, nextPoints);
      return { ...prev, [id]: { past, future } };
    });
  };

  const redoZoneGeometry = (id) => {
    if (id == null) return;
    setGeometryHistory((prev) => {
      const entry = prev[id];
      if (!entry || !entry.future || !entry.future.length) return prev;
      const future = [...entry.future];
      const nextPoints = future.shift();
      const current = getPolygonPoints(zones.find((z) => `${z.id}` === `${id}`));
      const past = current ? [...(entry.past || []), current] : entry.past || [];
      setZoneGeometry(id, nextPoints);
      return { ...prev, [id]: { past, future } };
    });
  };

  const groupZones = (ids = []) => {
    const valid = ids.filter((id) => id != null);
    if (!valid.length) return;
    const groupId = `group-${Date.now()}`;
    const parentId = valid[0];
    setZones((prev) =>
      prev.map((z) =>
        valid.includes(z.id)
          ? { ...z, groupId, parentId: `${z.id}` === `${parentId}` ? null : parentId }
          : z,
      ),
    );
  };

  const ungroupZones = (ids = []) => {
    const valid = ids.filter((id) => id != null);
    if (!valid.length) return;
    setZones((prev) =>
      prev.map((z) => (valid.includes(z.id) ? { ...z, groupId: null, parentId: null } : z)),
    );
  };

  const lockZones = (ids = [], locked = true) => {
    const valid = ids.filter((id) => id != null);
    if (!valid.length) return;
    setZones((prev) => prev.map((z) => (valid.includes(z.id) ? { ...z, locked } : z)));
  };

  const removeZone = (id) => {
    if (id == null) return;
    const targetKey = `${id}`;
    setZones((prev) => {
      const targetZone = prev.find((zone) => `${zone?.id}` === targetKey) || null;
      const descendantKeys = collectDescendantZoneKeys(prev, targetKey);
      const removedKeys = new Set([targetKey, ...descendantKeys]);
      if (descendantKeys.length > 0) {
        console.info('zones.parent_delete', {
          parentZoneId: targetKey,
          parentZoneName: targetZone?.name || null,
          childZoneIds: descendantKeys,
          removedZoneIds: Array.from(removedKeys),
        });
      }

      setActiveZoneId((prevActiveZoneId) =>
        removedKeys.has(`${prevActiveZoneId}`) ? null : prevActiveZoneId,
      );
      setSelectedZoneIds((prevSelectedZoneIds) =>
        (Array.isArray(prevSelectedZoneIds) ? prevSelectedZoneIds : []).filter(
          (selectedId) => !removedKeys.has(`${selectedId}`),
        ),
      );

      return prev.filter((zone) => !removedKeys.has(`${zone.id}`));
    });
  };

  const fetchZones = async () => {
    try {
      const res = await apiClient.get('/responder/zones');
      if (res.data) {
        const next = Array.isArray(res.data) ? res.data : [];
        setZones(next.map((z) => coerceZonePolygon(z, [])));
      }
      return res;
    } catch (error) {
      console.error('Error fetching zones:', error);
      throw error;
    }
  };

  const fetchZonesWithTours = async () => {
    try {
      const res = await apiClient.get('/responder/zones/with-tours');
      if (res.data) {
        const next = Array.isArray(res.data) ? res.data : [];
        setZonesWithTours(next.map((z) => coerceZonePolygon(z, [])));
      }
      return res;
    } catch (error) {
      console.error('Error fetching zones:', error);
      throw error;
    }
  };

  useEffect(() => {
    if (!shouldFetchZonesWithTours) return;
    fetchZonesWithTours().catch((error) => {
      console.warn('Failed to fetch zones with tours on initialization:', error);
    });
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldFetchZonesWithTours]);

  // Fetch zones automatically if no initial zones provided
  useEffect(() => {
    if (initialZones.length === 0 && zones.length === 0) {
      fetchZones().catch((error) => {
        console.warn('Failed to fetch zones on initialization:', error);
      });
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount

  const saveZones = async () => {
    return updateZones();
  };

  const updateZones = async () => {
    let hasAddOperation = false;
    try {
      const changedZones = zones.filter((zone) => zone.isModified);
      if (changedZones.length === 0) {
        return;
      }

      const duplicateZone = changedZones.find((zone) =>
        hasDuplicateEntityName(zones, zone?.name, zone?.serverId ?? zone?.id),
      );
      if (duplicateZone) {
        throw new Error(getDuplicateNameMessage('Zone'));
      }

      const existingZones = changedZones.filter((zone) => getPersistedId(zone) != null);
      const newZones = changedZones.filter((zone) => getPersistedId(zone) == null);
      hasAddOperation = newZones.length > 0;

      const createdIdMap = new Map();
      let createdResponse = null;
      let updatedResponse = null;
      const persistedIdByLocalKey = new Map();

      zones.forEach((zone) => {
        const persistedId = getPersistedId(zone);
        if (persistedId != null) {
          persistedIdByLocalKey.set(`${zone.id}`, persistedId);
        }
      });

      const resolvePersistedParentId = (parentId) => {
        if (parentId == null) return null;
        const mappedParentId = persistedIdByLocalKey.get(`${parentId}`);
        if (mappedParentId != null) return mappedParentId;
        const numericParentId = Number(parentId);
        return Number.isFinite(numericParentId) ? numericParentId : null;
      };

      if (newZones.length) {
        let remaining = [...newZones];

        while (remaining.length) {
          const ready = remaining.filter((zone) => {
            if (zone?.parentId == null) return true;
            return resolvePersistedParentId(zone.parentId) != null;
          });

          if (!ready.length) {
            throw new Error('Unable to create zones because parent IDs are unresolved.');
          }

          const payload = serializeZones(
            ready.map((zone) => ({
              ...zone,
              id: undefined,
              parentId: resolvePersistedParentId(zone?.parentId),
            })),
          );
          createdResponse = await apiClient.post('/responder/zones', payload);
          const createdData = createdResponse?.data;

          const createdBatch = Array.isArray(createdData)
            ? createdData
            : createdData && typeof createdData === 'object'
              ? [createdData]
              : [];

          ready.forEach((zone, idx) => {
            const created = createdBatch[idx] ?? null;
            if (created?.id != null) {
              createdIdMap.set(`${zone.id}`, created.id);
              persistedIdByLocalKey.set(`${zone.id}`, created.id);
            }
          });

          const readyIds = new Set(ready.map((zone) => `${zone.id}`));
          remaining = remaining.filter((zone) => !readyIds.has(`${zone.id}`));
        }
      }

      if (existingZones.length) {
        const payload = serializeZones(
          existingZones.map((zone) => ({
            ...zone,
            id: getPersistedId(zone),
            parentId: resolvePersistedParentId(zone?.parentId),
          })),
        );
        updatedResponse = await apiClient.put('/responder/zones', payload);
      }

      if (newZones.length || existingZones.length) {
        setZones((prev) =>
          prev.map((zone) => {
            const wasChanged = changedZones.some((z) => `${z.id}` === `${zone.id}`);
            if (!wasChanged) return zone;
            const serverId = createdIdMap.get(`${zone.id}`) ?? zone.serverId;
            return { ...zone, serverId, isModified: false };
          }),
        );

        // Show appropriate success toast based on operation types
        if (newZones.length && existingZones.length) {
          notifySuccessToast('Zones', { isAddOperation: false }); // Mixed operations
        } else if (newZones.length) {
          notifySuccessToast('Zone', { isAddOperation: true });
        } else {
          notifySuccessToast('Zone', { isAddOperation: false });
        }
      }

      return { createdResponse, updatedResponse };
    } catch (error) {
      console.error('Error saving zones:', error);
      notifyErrorToast('Zone', { isAddOperation: hasAddOperation }, { errorPayload: error });
      throw error;
    }
  };

  const deleteZone = async (id) => {
    try {
      const targetKey = `${id}`;
      const descendantKeys = collectDescendantZoneKeys(zones, targetKey);
      const keysToDelete = [targetKey, ...descendantKeys];
      const zoneByKey = new Map(zones.map((zone) => [`${zone?.id}`, zone]));
      const sortedKeys = keysToDelete
        .filter((key, index, arr) => arr.indexOf(key) === index)
        .sort((a, b) => {
          const depthA = Number(zoneByKey.get(a)?.depth ?? zoneByKey.get(a)?.layer ?? 0);
          const depthB = Number(zoneByKey.get(b)?.depth ?? zoneByKey.get(b)?.layer ?? 0);
          return depthB - depthA;
        });

      let res = null;
      for (const key of sortedKeys) {
        const zone = zoneByKey.get(key) || null;
        const persistedId = getPersistedId(zone);
        if (persistedId == null) continue;
        res = await apiClient.delete(`/responder/zones/${persistedId}`);
      }

      // Remove from local state after successful backend deletion(s)
      removeZone(id);
      notifySuccessToast('Zone', { isAddOperation: false, isDeleteOperation: true });
      return res;
    } catch (error) {
      console.error('Error deleting zone:', error);
      notifyErrorToast(
        'Zone',
        { isAddOperation: false, isDeleteOperation: true },
        { errorPayload: error },
      );
      throw error;
    }
  };

  const value = useMemo(
    () => ({
      zones,
      zonesWithMeta,
      zonesWithTours,
      setZonesWithTours,
      setZones,
      activeZoneId,
      setActiveZoneId,
      selectedZoneIds,
      setSelectedZoneIds,
      zonesEditing,
      setZonesEditing,
      zoneEditMode,
      setZoneEditMode,
      pushGeometryHistory,
      undoZoneGeometry,
      redoZoneGeometry,
      groupZones,
      ungroupZones,
      lockZones,
      addZone,
      updateZone,
      removeZone,
      saveZones,
      updateZones,
      fetchZones,
      deleteZone,
    }),
    //eslint-disable-next-line react-hooks/exhaustive-deps
    [activeZoneId, selectedZoneIds, zones, zonesEditing, zonesWithMeta, zoneEditMode],
  );

  return <ZonesContext.Provider value={value}>{children}</ZonesContext.Provider>;
};

export const useZones = () => {
  const ctx = useContext(ZonesContext);
  if (!ctx) {
    throw new Error('useZones must be used within a ZonesProvider');
  }
  return ctx;
};

export { ZonesContext };
